import piexif from 'piexifjs';

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  preserveExif?: boolean;
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 2048,
  maxHeight: 2048,
  quality: 0.8,
  preserveExif: true,
};

/**
 * Compresses an image file while preserving EXIF metadata (including GPS data).
 * Resizes to max dimensions and applies JPEG compression.
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Skip non-image files or already small files
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // If file is already small enough (under 500KB), skip compression
  if (file.size < 500 * 1024) {
    return file;
  }

  try {
    // Read the original file
    const arrayBuffer = await file.arrayBuffer();
    const originalDataUrl = await fileToDataUrl(file);

    // Extract EXIF data if preserving
    let exifData: string | null = null;
    if (opts.preserveExif && (file.type === 'image/jpeg' || file.type === 'image/jpg')) {
      try {
        exifData = piexif.dump(piexif.load(originalDataUrl));
      } catch (e) {
        console.warn('Could not extract EXIF data:', e);
      }
    }

    // Create image bitmap for resizing
    const blob = new Blob([arrayBuffer], { type: file.type });
    const imageBitmap = await createImageBitmap(blob);

    // Calculate new dimensions maintaining aspect ratio
    let { width, height } = imageBitmap;
    const aspectRatio = width / height;

    if (width > opts.maxWidth) {
      width = opts.maxWidth;
      height = Math.round(width / aspectRatio);
    }
    if (height > opts.maxHeight) {
      height = opts.maxHeight;
      width = Math.round(height * aspectRatio);
    }

    // Create canvas and draw resized image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Use high-quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(imageBitmap, 0, 0, width, height);

    // Export as JPEG with quality setting
    let compressedDataUrl = canvas.toDataURL('image/jpeg', opts.quality);

    // Re-inject EXIF data if we have it
    if (exifData && opts.preserveExif) {
      try {
        compressedDataUrl = piexif.insert(exifData, compressedDataUrl);
      } catch (e) {
        console.warn('Could not re-inject EXIF data:', e);
      }
    }

    // Convert data URL back to File
    const compressedBlob = dataUrlToBlob(compressedDataUrl);
    const compressedFile = new File(
      [compressedBlob],
      file.name.replace(/\.[^.]+$/, '.jpg'), // Ensure .jpg extension
      { type: 'image/jpeg', lastModified: Date.now() }
    );

    console.log(
      `Compressed ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${Math.round((1 - compressedFile.size / file.size) * 100)}% reduction)`
    );

    return compressedFile;
  } catch (error) {
    console.error('Image compression failed, using original:', error);
    return file;
  }
}

/**
 * Compress multiple images in parallel
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  return Promise.all(files.map(file => compressImage(file, options)));
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}
